"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  RadarChart,
  type RadarSeries,
} from "@/components/tools/color-charts/radar-svg";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { MAX_SERIES } from "@/lib/tool-kit/charts/color";
import { type NumberFormat, valueText } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type LabeledTable,
  parseLabeledRows,
  seriesNames,
} from "@/lib/tool-kit/charts/parse";
import {
  type RadarScaling,
  radarExtremes,
  seriesFromRows,
  sharedScale,
} from "@/lib/tool-kit/color-charts/radar";

/** English strings. A translated content file overrides these through `ui`. */
export const RADAR_CHART_MAKER_STRINGS = {
  dataLabel: "Your data — one axis per line: label, value",
  dataHint:
    "Add more values on a line for more series. Separate cells with commas, tabs or semicolons — use semicolons if your numbers have decimal commas. A first line without numbers names the series. The axes are drawn clockwise from the top in the order of the lines.",
  exampleData:
    "Criterion, Draft 1, Draft 2\nArgument, 3, 4\nEvidence, 2, 4\nOrganization, 4, 4\nStyle, 3, 3\nMechanics, 4, 5\nCitations, 2, 4",
  firstLineHeader: "The first line names the series",
  exampleTitle: "Essay Rubric Scores, Draft 1 and Draft 2",
  axesDetected: "one:{count} axis|other:{count} axes",
  seriesDetected: "one:{count} series|other:{count} series",
  seriesDefault: "Series {number}",
  scalingLabel: "Scale",
  scaleShared: "One scale for every axis",
  scalePerAxis: "Each axis as a share of its own maximum",
  scaleHint:
    "A shared scale keeps the radius proportional to the value, so use it when the axes share a unit. The per-axis scale lets axes in different units share a chart, but the shape then shows each axis against its own maximum — not which axis is larger.",
  scaleMin: "Scale minimum",
  scaleMax: "Scale maximum",
  scaleAuto: "Auto",
  scaleBad:
    "The minimum must be below the maximum, so the automatic scale is used.",
  fillLabel: "Fill the shapes",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  colorLabel: "Color of the first series",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Show the values at each point",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the data as a table",
  axisColumn: "Axis",
  chartLabel: "Radar chart",
  chartDescription:
    "Radar chart with {axes} and {series}. Highest value: {maxSeries} on {maxAxis}, {max}. Lowest value: {minSeries} on {minAxis}, {min}.",
  perAxisName: "{axis} (100% = {max})",
  enterData:
    "Enter at least three lines, each with a label and a number, to draw the chart.",
  needThree:
    "A radar chart needs at least three axes. With two, the shape is a line; with one, a point.",
  tooManyAxes:
    "With more than {max} axes the names crowd and the shape is hard to read. A bar graph or a table may serve better.",
  skippedLines:
    "one:Line {lines} has no number and was left out.|other:Lines {lines} have no numbers and were left out.",
  invalidCells:
    "one:{count} cell is not a number and leaves a gap in the outline.|other:{count} cells are not numbers and leave gaps in the outline.",
  tooManySeries:
    "Only the first {max} series are drawn — beyond that, colors can no longer be told apart.",
  negativePerAxis:
    "Negative values are drawn at the center in this mode. Switch to one scale to show them.",
} as const;

type Strings = typeof RADAR_CHART_MAKER_STRINGS;

const MISSING = "—";
const CROWDED_AXES = 12;
const MAX_AXES = 30;

/** The `<desc>` a screen reader announces: counts, and the highest and lowest points. */
function summarize(
  axes: string[],
  series: RadarSeries[],
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string {
  const { max, min } = radarExtremes(axes, series);
  return t(strings.chartDescription, {
    axes: tPlural(strings.axesDetected, locale, axes.length, {
      count: fmt(axes.length),
    }),
    series: tPlural(strings.seriesDetected, locale, series.length, {
      count: fmt(series.length),
    }),
    maxSeries: max?.name ?? "",
    maxAxis: max?.axis ?? "",
    max: max ? valueText(fmt, max.value) : "",
    minSeries: min?.name ?? "",
    minAxis: min?.axis ?? "",
    min: min ? valueText(fmt, min.value) : "",
  });
}

function radarNotices(
  table: LabeledTable,
  axisCount: number,
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string[] {
  const notes: string[] = [];
  if (table.rows.length === 0) {
    notes.push(strings.enterData);
  } else if (axisCount < 3) {
    notes.push(strings.needThree);
  }
  if (axisCount > CROWDED_AXES) {
    notes.push(t(strings.tooManyAxes, { max: fmt(CROWDED_AXES) }));
  }
  if (table.skipped.length > 0) {
    notes.push(
      tPlural(strings.skippedLines, locale, table.skipped.length, {
        lines: table.skipped.map((n) => fmt(n)).join(", "),
      })
    );
  }
  if (table.invalidCells > 0) {
    notes.push(
      tPlural(strings.invalidCells, locale, table.invalidCells, {
        count: fmt(table.invalidCells),
      })
    );
  }
  if (table.seriesCount > MAX_SERIES) {
    notes.push(t(strings.tooManySeries, { max: fmt(MAX_SERIES) }));
  }
  return notes;
}

export default function RadarChartMakerWidget() {
  const strings = useToolUi(RADAR_CHART_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [headerChoice, setHeaderChoice] = useState<boolean | null>(null);
  const [scaling, setScaling] = useState<RadarScaling>("shared");
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const [fill, setFill] = useState(true);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const table = parseLabeledRows(raw, { header: headerChoice ?? undefined });
  const seriesCount = Math.min(table.seriesCount, MAX_SERIES);
  const rows = table.rows.slice(0, MAX_AXES);
  const names = seriesNames(table, (index) =>
    t(strings.seriesDefault, { number: fmt(index + 1) })
  ).slice(0, seriesCount);
  const axes = rows.map((row) => row.label);
  const values = seriesFromRows(rows, seriesCount);
  const series: RadarSeries[] = names.map((name, index) => ({
    name,
    values: values[index] ?? [],
  }));
  const finite = values.flat().filter((v): v is number => v !== null);
  const scale = sharedScale(
    finite,
    parseNumberInput(low),
    parseNumberInput(high)
  );
  const drawable = axes.length >= 3 && series.length > 0;
  const hasNegative = finite.some((v) => v < 0);
  const head = [table.header?.[0]?.trim() || strings.axisColumn, ...names];
  const notices = radarNotices(table, axes.length, strings, locale, fmt);
  if (scaling === "perAxis" && hasNegative) {
    notices.push(strings.negativePerAxis);
  }
  if (scaling === "shared" && scale.bad) {
    notices.push(strings.scaleBad);
  }

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="rcm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={8}
        value={raw}
      />
      <Checkbox
        checked={headerChoice ?? table.autoHeader}
        label={strings.firstLineHeader}
        onChange={setHeaderChoice}
      />
      <div aria-live="polite" className="space-y-2">
        {axes.length > 0 ? (
          <p className={HINT}>
            {tPlural(strings.axesDetected, locale, axes.length, {
              count: fmt(axes.length),
            })}
            {" · "}
            {tPlural(strings.seriesDetected, locale, seriesCount, {
              count: fmt(seriesCount),
            })}
          </p>
        ) : null}
        {notices.map((notice) => (
          <Notice key={notice}>{notice}</Notice>
        ))}
      </div>
      <div className="space-y-3">
        <PillGroup
          label={strings.scalingLabel}
          name="rcm-scaling"
          onChange={setScaling}
          options={[
            { value: "shared", label: strings.scaleShared },
            { value: "perAxis", label: strings.scalePerAxis },
          ]}
          value={scaling}
        />
        <p className={HINT}>{strings.scaleHint}</p>
        {scaling === "shared" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="rcm-min"
              label={strings.scaleMin}
              numeric
              onChange={setLow}
              placeholder={strings.scaleAuto}
              value={low}
            />
            <TextField
              id="rcm-max"
              label={strings.scaleMax}
              numeric
              onChange={setHigh}
              placeholder={strings.scaleAuto}
              value={high}
            />
          </div>
        ) : null}
        <Checkbox checked={fill} label={strings.fillLabel} onChange={setFill} />
      </div>
      {drawable ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([
            head,
            ...axes.map((axis, i) => [
              axis,
              ...series.map((s) => s.values[i] ?? null),
            ]),
          ])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "radar-chart")}
          labels={strings}
          note={style.note}
          table={{
            head,
            rows: axes.map((axis, i) => [
              axis,
              ...series.map((s) => {
                const v = s.values[i];
                return v === null || v === undefined
                  ? MISSING
                  : valueText(fmt, v);
              }),
            ]),
          }}
          title={style.title}
        >
          <RadarChart
            axes={axes}
            axisMaxText={(axis, max) => t(strings.perAxisName, { axis, max })}
            description={summarize(axes, series, strings, locale, fmt)}
            fill={fill}
            fmt={fmt}
            id="rcm-chart"
            label={style.title.trim() || strings.chartLabel}
            scale={scale}
            scaling={scaling}
            series={series}
            style={style}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        colorLabels={strings}
        idPrefix="rcm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      />
    </div>
  );
}
